import { DispatchContext, StateContext } from "@client/pages/round/Round";
import { pushRecentProposal } from "@client/utils/recentProposals";
import {
  assertGetEvaluator,
  getActiveEvaluatorEvaluations,
  getActiveProposals,
  getEvaluableOrPlaceholder,
  getEvaluatorTrancheList,
  mvfProfileFromDeferrals,
  resolveDeferredEvaluators,
} from "@engine/round/accessors";
import { Action } from "@engine/round/actions";
import type { Evaluation, RoundAggregate } from "@engine/round/aggregate";
import { runSProcessOnRoundWithMatchingAdjustment } from "@lib/algoInterface/runSProcessOnRound";
import { defaultExpectedMatchingFraction } from "@lib/constants";
import { MVF, mvfProfileFromTrancheProfile } from "@lib/tranche";
import { fdvFromMVF } from "@lib/tranche/trancheUtils";
import type { Evaluable, Evaluator } from "@lib/types";
import { calculateShrunkInternalDollar } from "algorithm/matching";
import Matrix from "algorithm/matrix";
import { useContext, useEffect, useMemo, useRef, useState } from "react";

type FrontendEvaluationLookup = { [evaluableId: string]: FrontendEvaluation };

export type FrontendEvaluation = Evaluation & {
  evaluable: Evaluable;
  evaluatorId: string;
  shortname: string;
  mvf: MVF;
  fdv: number;
  isDeferred: boolean;
};

export type MVFUpdate = (
  evaluatorId: string,
  evaluableId: string,
  mvf: MVF,
) => void;

export type EvaluationsCache = {
  mvfUpdate: MVFUpdate;
  mvfCommit: () => void;
  evaluations: FrontendEvaluationLookup;
  getEvaluatorEvaluations: (evaluatorId: string) => FrontendEvaluation[];
};

type MVFBeingEdited = {
  mvf: MVF;
  evaluatorId: string;
  evaluableId: string;
};

// This exists so that users can drag MVF points around without generating thousands of actions.
export function useEvaluatorEvaluationsCache(
  evaluator: Evaluator,
  useMatchingAdjustment: boolean,
): EvaluationsCache {
  const dispatch = useContext(DispatchContext);
  const { round, subscribe, unsubscribe, getRound } = useContext(StateContext);

  const mvfBeingEditedRef = useRef<MVFBeingEdited | null>(null);

  const {
    initialSProcessOutput,
    sProcessOutputWithMatchingAdjustment,
    multipliers,
  } = useMemo(() => {
    return runSProcessOnRoundWithMatchingAdjustment(round);
  }, [round]);

  const matchingAdjustmentMultipliers = multipliers.rename(
    "proposal -> proposal",
    "evaluator -> evaluation",
  );

  const sProcessOutput = useMatchingAdjustment
    ? sProcessOutputWithMatchingAdjustment
    : initialSProcessOutput;

  const evaluatorIndex =
    sProcessOutput.layerNodes[0].findIndex((node) => node.id === evaluator.id) -
    1; // -1 for the hold proposal

  const [evaluations, setEvaluations] = useState<FrontendEvaluationLookup>(() =>
    getEvaluationDisplayMap(
      round,
      evaluator,
      evaluatorIndex,
      mvfBeingEditedRef.current,
      matchingAdjustmentMultipliers,
      useMatchingAdjustment,
    ),
  );

  // if the mvf being edited is changed by an event, we need to erase the local
  // edits to that mvf so that the new state is not overwritten when committing
  // stale changes to the mvf
  //
  // NOTE: this should be VERY unlikely to happen, but failing to handle this
  // case will result in silent unintuitive behavior
  //
  // There are other events that may relate to the mvf being edited, however
  // not handling them might only lead to an error when committing, or some
  // unintuitive ordering of events (for example: an mvf being changed after
  // the evaluator has been recused)
  useEffect(() => {
    function handleAction(action: Action) {
      setEvaluations(
        getEvaluationDisplayMap(
          getRound(),
          evaluator,
          evaluatorIndex,
          mvfBeingEditedRef.current,
          matchingAdjustmentMultipliers,
          useMatchingAdjustment,
        ),
      );

      const evaluatorId =
        "evaluatorId" in action
          ? action.evaluatorId
          : "memberId" in action
            ? action.memberId
            : null;

      if (
        "evaluableId" in action &&
        mvfBeingEditedRef.current &&
        mvfBeingEditedRef.current.evaluatorId === evaluatorId &&
        mvfBeingEditedRef.current.evaluableId === action.evaluableId
      ) {
        mvfBeingEditedRef.current = null;
      }
    }

    subscribe("EVALUATION_MVF_CHANGE", handleAction);
    subscribe("EVALUATION_SET_ZERO", handleAction);
    subscribe("NORMALIZE_EVALUATIONS", handleAction);
    subscribe("FUNDER_BUDGET_CHANGE", handleAction);
    subscribe("SET_EXPECTED_MATCHING_FRACTION", handleAction);
    subscribe("SET_TRANCHE_ALLOCATION", handleAction);
    subscribe("SET_TRANCHE_HEIGHT", handleAction);
    subscribe("ADD_TRANCHE", handleAction);
    subscribe("REMOVE_TRANCHE", handleAction);
    subscribe("START_EVALUATION", handleAction);
    subscribe("REMOVE_PROPOSAL", handleAction);
    subscribe("ROUND_MEMBER_PROPERTY_CHANGE", handleAction);
    subscribe(
      "RECOMMENDER_OUTSIDE_DOLLAR_SUBSTITUTION_RATE_CHANGE",
      handleAction,
    );
    subscribe("EVALUATION_STATUS_CHANGE", handleAction);
    subscribe("INCREMENTAL_EVALS_PUSH", handleAction);
    subscribe("INCREMENTAL_EVALS_UNDO", handleAction);
    subscribe("INCREMENTAL_EVALS_REDO", handleAction);

    return () => {
      unsubscribe("EVALUATION_MVF_CHANGE", handleAction);
      unsubscribe("EVALUATION_SET_ZERO", handleAction);
      unsubscribe("NORMALIZE_EVALUATIONS", handleAction);
      unsubscribe("FUNDER_BUDGET_CHANGE", handleAction);
      unsubscribe("SET_EXPECTED_MATCHING_FRACTION", handleAction);
      unsubscribe("SET_TRANCHE_ALLOCATION", handleAction);
      unsubscribe("SET_TRANCHE_HEIGHT", handleAction);
      unsubscribe("ADD_TRANCHE", handleAction);
      unsubscribe("REMOVE_TRANCHE", handleAction);
      unsubscribe("START_EVALUATION", handleAction);
      unsubscribe("REMOVE_PROPOSAL", handleAction);
      unsubscribe("ROUND_MEMBER_PROPERTY_CHANGE", handleAction);
      unsubscribe(
        "RECOMMENDER_OUTSIDE_DOLLAR_SUBSTITUTION_RATE_CHANGE",
        handleAction,
      );
      unsubscribe("EVALUATION_STATUS_CHANGE", handleAction);
      unsubscribe("INCREMENTAL_EVALS_PUSH", handleAction);
      unsubscribe("INCREMENTAL_EVALS_UNDO", handleAction);
      unsubscribe("INCREMENTAL_EVALS_REDO", handleAction);
    };

    // any round aggregate event will cause the round aggregate to change (an
    // applied event will produce a new aggregate)
    //
    // events are emitted after the render which changed the round aggregate,
    // so the 'round' reference by the event handlers will be up to date
    //
    // TODO: A better way of building this cache would be to avoid relying on
    // the state of the round aggregate and instead building up the cache state
    // from events. Basically relevants would have their own reducer which
    // updates the cache state. This however is a lot more work.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluator.id, subscribe, unsubscribe, getRound]);

  // TODO: Some of the UI is still using state from the round aggregate directly
  // instead of this cache (like for inactive evaluables), this results in some
  // UI updates happening a slightly different times, leading to visual
  // noise/flicker. This is most easily reproduced by starting an evaluation
  useEffect(() => {
    setEvaluations(
      getEvaluationDisplayMap(
        round,
        evaluator,
        evaluatorIndex,
        mvfBeingEditedRef.current,
        matchingAdjustmentMultipliers,
        useMatchingAdjustment,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluator.id, useMatchingAdjustment]);

  function mvfUpdate(evaluatorId: string, evaluableId: string, mvf: MVF) {
    const mvfBeingEdited = mvfBeingEditedRef.current;

    if (
      mvfBeingEdited &&
      (mvfBeingEdited.evaluatorId !== evaluatorId ||
        mvfBeingEdited.evaluableId !== evaluableId)
    ) {
      void dispatch({
        type: "EVALUATION_MVF_CHANGE",
        evaluatorId: mvfBeingEdited.evaluatorId,
        evaluableId: mvfBeingEdited.evaluableId,
        mvf: mvfBeingEdited.mvf,
      });
      pushRecentProposal(mvfBeingEdited.evaluableId);
    }

    mvfBeingEditedRef.current = {
      evaluatorId,
      evaluableId,
      mvf,
    };

    const newLookup: FrontendEvaluationLookup = {
      ...evaluations,
      [evaluableId]: {
        ...evaluations[evaluableId],
        mvf,
        fdv: fdvFromMVF(mvf),
      },
    };

    setEvaluations(newLookup);
  }

  function mvfCommit() {
    const mvfBeingEdited = mvfBeingEditedRef.current;
    if (!mvfBeingEdited) {
      return;
    }

    void dispatch({
      type: "EVALUATION_MVF_CHANGE",
      evaluatorId: mvfBeingEdited.evaluatorId,
      evaluableId: mvfBeingEdited.evaluableId,
      mvf: mvfBeingEdited.mvf,
    });
    pushRecentProposal(mvfBeingEdited.evaluableId);

    mvfBeingEditedRef.current = null;
  }

  function getEvaluatorEvaluations(): FrontendEvaluation[] {
    return Object.values(evaluations);
  }

  return {
    mvfUpdate,
    mvfCommit,
    evaluations,
    getEvaluatorEvaluations,
  };
}

function getEvaluationDisplayMap(
  round: RoundAggregate,
  evaluator: Evaluator,
  evaluatorIndex: number,
  mvfBeingEdited: MVFBeingEdited | null,
  matchingAdjustmentMultipliers: Matrix<"proposal", "evaluation">,
  useMatchingAdjustment: boolean,
): FrontendEvaluationLookup {
  const evaluations: FrontendEvaluationLookup = {};
  const outsideDollarSubstitutionRate =
    evaluator.type === "recommender"
      ? evaluator.outsideDollarSubstitutionRate
      : 0;

  const evaluatorTrancheList = getEvaluatorTrancheList(round, evaluator);
  const mvfs = mvfProfileFromTrancheProfile(evaluatorTrancheList);

  const activeProposals = getActiveProposals(round);
  const activeProposalsWithoutHold = activeProposals.filter(
    (proposal) => proposal.id !== round.holdProposalId,
  );

  const evaluatorEvaluations = getActiveEvaluatorEvaluations(round, evaluator);

  const matchingAdjustmentMultipliers_ = useMatchingAdjustment
    ? matchingAdjustmentMultipliers
    : Matrix.ones(
        activeProposalsWithoutHold.length,
        evaluatorEvaluations.length,
      );

  const matchInfo = activeProposalsWithoutHold.map((proposal) => ({
    matchRate: proposal.typedFields.matchRate ?? 0,
    baselineStandardGrantAmount:
      proposal.typedFields.baselineStandardGrantAmount ?? 0,
    targetOutsideDonations: proposal.typedFields.targetOutsideDonations ?? 0,
  }));

  for (const evaluation of getActiveEvaluatorEvaluations(round, evaluator)) {
    const evaluableId = evaluation.evaluableId;
    const useMvfBeingEdited =
      mvfBeingEdited &&
      mvfBeingEdited.evaluatorId === evaluator.id &&
      mvfBeingEdited.evaluableId === evaluableId;
    let mvf: MVF = useMvfBeingEdited ? mvfBeingEdited.mvf : mvfs[evaluableId];

    if (!mvf) {
      continue;
    }

    if (useMatchingAdjustment && evaluableId !== round.holdProposalId) {
      const nodeIndex = activeProposalsWithoutHold.findIndex(
        (proposal) => proposal.id === evaluableId,
      );
      const { matchRate, baselineStandardGrantAmount, targetOutsideDonations } =
        matchInfo[nodeIndex];
      const maxMatchingDollars = targetOutsideDonations * matchRate;

      mvf = mvf.map((point) => {
        const dollar = calculateShrunkInternalDollar(
          point.dollar,
          baselineStandardGrantAmount,
          maxMatchingDollars,
          matchRate,
          outsideDollarSubstitutionRate,
          evaluation.expectedMatchingFraction ??
            defaultExpectedMatchingFraction,
        );

        const value =
          point.value *
          matchingAdjustmentMultipliers_.get(nodeIndex, evaluatorIndex);

        return {
          value,
          dollar,
          placedManually: point.placedManually,
        };
      });
    }

    const evaluable = getEvaluableOrPlaceholder(round, evaluation.evaluableId);

    evaluations[evaluation.evaluableId] = {
      evaluable,
      evaluatorId: evaluator.id,
      shortname: evaluable.shortname,
      ...evaluation,
      mvf,
      fdv: fdvFromMVF(mvf),
      isDeferred: false,
    };
  }

  // Also show all deferred evaluations
  if (evaluator.type === "recommender") {
    const deferralEvaluators = resolveDeferredEvaluators(round, evaluator);
    const allDeferralEvaluatorIds = new Set(Object.values(deferralEvaluators));
    const deferredEvaluationsByEvaluatorId: {
      [evaluatorId: string]: Evaluation[];
    } = {};
    for (const evaluatorId of allDeferralEvaluatorIds) {
      const evaluator = assertGetEvaluator(round, evaluatorId);
      deferredEvaluationsByEvaluatorId[evaluatorId] =
        getActiveEvaluatorEvaluations(round, evaluator);
    }

    const deferredMvfs = mvfProfileFromDeferrals(evaluator, round);
    for (const [evaluableId, mvf] of Object.entries(deferredMvfs)) {
      const evaluable = getEvaluableOrPlaceholder(round, evaluableId);
      const evaluation: Evaluation = deferredEvaluationsByEvaluatorId[
        deferralEvaluators[evaluableId]
      ].find((e) => e.evaluableId === evaluableId)!;
      evaluations[evaluableId] = {
        evaluable,
        evaluatorId: deferralEvaluators[evaluableId],
        shortname: evaluable.shortname,
        ...evaluation,
        mvf,
        fdv: fdvFromMVF(mvf),
        isDeferred: true,
      };
    }
  }

  return evaluations;
}
